'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useCatalogContext } from '@marketplace-web/catalog/catalog-page-data'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  getClosetPromotions,
  transformClosetPromotionsResponse,
  ClosetModel,
  VasEntryPoint,
  VasEntryPointModel,
  parseCatalogUrlParams,
} from '@marketplace-web/vas/vas-data'

import { ClosetPreviewLocation, getIsClosetPreviewSeen } from '../utils/closetPreviewCap'
import useFetchVasEntryPoints from './useFetchVasEntryPoints'

const CLOSET_PROMOTIONS_COUNT = 4

const useClosetPromotion = () => {
  const currentUserId = useSession()?.user?.id
  const searchSessionId = useCatalogContext().state.items.searchTrackingParams?.sessionId

  const { searchParams, relativeUrl } = useBrowserNavigation()

  const currentFilterParams = parseCatalogUrlParams(searchParams, relativeUrl)
  const previousFilterParamsRef = useRef(currentFilterParams)

  const stableFilterParams = useMemo(() => {
    if (!isEqual(currentFilterParams, previousFilterParamsRef.current)) {
      previousFilterParamsRef.current = currentFilterParams

      return currentFilterParams
    }

    return previousFilterParamsRef.current
  }, [currentFilterParams])
  const searchByImageUuid = searchParams.search_by_image_uuid

  const isClosetPromotionPreviewCatalogV2AbTestOn =
    useAbTest('closet_promotion_preview_catalog_v2')?.variant === 'on'
  const isClosetPreviewSeen = getIsClosetPreviewSeen(ClosetPreviewLocation.CatalogAndFeed)

  let isClosetPromotionKillswitchEnabled = useFeatureSwitch('killswitch_closet_promotion_web')
  // search by image feature does not support closet promotions
  isClosetPromotionKillswitchEnabled ||= !!searchByImageUuid

  const userIds = useRef<Array<number>>([])
  const hasMore = useRef(true)

  const [closets, setClosets] = useState<Array<ClosetModel>>([])

  const resetClosets = () => {
    userIds.current = []
    hasMore.current = true
  }

  useEffect(() => {
    resetClosets()
  }, [stableFilterParams])

  const { data: vasEntryPoints, fetchClosetPromotionEntryPoint } = useFetchVasEntryPoints(
    [VasEntryPoint.PromotedClosets],
    isClosetPromotionPreviewCatalogV2AbTestOn && !isClosetPreviewSeen,
  )

  const fetchClosetPromotions = useCallback(
    async (isPreview: boolean) => {
      const shouldFetch = !isClosetPromotionKillswitchEnabled && hasMore.current

      if (!shouldFetch) {
        setClosets([])

        return
      }

      const response = await getClosetPromotions({
        perPage: CLOSET_PROMOTIONS_COUNT,
        screenName: 'catalog',
        excludedUserIds: userIds.current,
        searchSessionId,
        isPreview: isPreview || undefined,
        catalogFilterParams: stableFilterParams,
      })

      if ('errors' in response) {
        setClosets([])

        return
      }

      hasMore.current = response.page_info.has_more
      const transformedClosets = transformClosetPromotionsResponse(response)

      if (transformedClosets[0]) {
        transformedClosets[0].showBanner = true

        const isViewingOwnCloset = currentUserId === transformedClosets[0].user.id

        if (isPreview && isViewingOwnCloset) transformedClosets[0].isPreview = true
      }

      userIds.current = [...userIds.current, ...transformedClosets.map(closet => closet.user.id)]
      setClosets(transformedClosets)
    },
    [isClosetPromotionKillswitchEnabled, searchSessionId, stableFilterParams, currentUserId],
  )

  const fetchData = useLatestCallback(async () => {
    let closetPromotionEntryPoint: VasEntryPointModel | undefined

    if (!vasEntryPoints && !isClosetPreviewSeen && isClosetPromotionPreviewCatalogV2AbTestOn) {
      closetPromotionEntryPoint = await fetchClosetPromotionEntryPoint()
    }

    fetchClosetPromotions(!!closetPromotionEntryPoint)
  })

  useEffect(() => {
    fetchData()
  }, [searchSessionId, fetchData])

  return { closetsWithBanner: closets, vasEntryPoints }
}

export default useClosetPromotion
